import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Message, Popconfirm, Table, Tooltip, Typography } from '@arco-design/web-react';
import { IconStar, IconStarFill } from '@arco-design/web-react/icon';
import { PillTag } from '../../../components';
import { SkeletonTable } from '../../../components/Skeleton';
import { usePhoneStore } from '../../../store/phone';
import { executeAssistAction } from '../../../api/assist';
import { listSkillGroups, type SkillGroup } from '../../../api/skillGroups';
import { type AgentProfile } from '../../../api/agent';
import { listMonitorAgents, listMonitorQueues, type MonitorAgent, type MonitorQueue } from '../../../api/monitor';
import { transferAgentStateMeta } from '../../../utils/labels';
import { SHIFT_TABS, readTransferFavs, writeTransferFavs, type RingTarget, type TransferTargetType } from './_transferTargets';

// 坐席/话机实时状态的文案与色统一走 labels.ts 的 transferAgentStateMeta（转接面板口径：
// 振铃/通话中/示忙统一显示「示忙」，避免坐席误判可转接），此处不再保留页内映射表。

/**
 * 转接面板：技能组/坐席/话机 三目标胶囊 Tab + 紧凑表格（sticky 表头 + 行 hover + text 主色转接）。
 * 视觉口径：胶囊 tab 与顶栏胶囊同语言；表头主色 8% + 细描边圆角容器（全站表格口径）。
 */
export default function TransferPopover({
  onClose,
  disabled,
  onTransferred,
  ringCallUUIDRef,
  intent = 'blind',
}: {
  onClose: () => void;
  disabled?: boolean;
  onTransferred?: () => void;
  ringCallUUIDRef: { current: string };
  /** blind=盲转；consult=咨询邀请（先 hold 客户再拨第二路） */
  intent?: 'blind' | 'consult';
}) {
  const { refer, inviteExpert, status } = usePhoneStore();
  const [tab, setTab] = useState<TransferTargetType>('skill_group');
  const [groups, setGroups] = useState<SkillGroup[]>([]);
  const [monitorAgents, setMonitorAgents] = useState<MonitorAgent[]>([]);
  const [agentStates, setAgentStates] = useState<Record<number, MonitorAgent>>({});
  const [queueLive, setQueueLive] = useState<Record<string, MonitorQueue>>({});
  const [loading, setLoading] = useState(true); // 默认骨架屏：先占位再查询，避免首帧露出空态提示
  /** 坐席/话机目标的搜索关键字（本地过滤，输入不触发请求） */
  const [targetKw, setTargetKw] = useState('');
  const fetchedRef = useRef(false);

  /**
   * 转接候选坐席 = 监控墙返回的全量坐席（同一份数据已含实时状态）。
   * 刻意不再单独请求 `/agents` 的限量列表：那个接口按页取数，坐席量级一大候选就只剩前若干人，
   * 排在后面的坐席永远转接不到，且界面上看不出被截断。
   */
  const agents = useMemo<AgentProfile[]>(
    () => monitorAgents.map((m) => ({ id: m.agent_id, name: m.name, extension: m.extension, state: m.state })),
    [monitorAgents],
  );
  /** 收藏目标 key：技能组 g:<队列键 code>，坐席/话机 a:<extension> */
  const [favs, setFavs] = useState<string[]>(readTransferFavs);
  const toggleFav = (key: string) => {
    setFavs((prev) => {
      const next = prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key];
      writeTransferFavs(next);
      return next;
    });
  };
  const favStar = (key: string) => (
    <Button
      size="mini"
      type="text"
      aria-label={favs.includes(key) ? '取消收藏' : '收藏'}
      onClick={(e) => {
        e.stopPropagation();
        toggleFav(key);
      }}
    >
      {favs.includes(key) ? <IconStarFill style={{ color: 'var(--bcc-warning)' }} /> : <IconStar />}
    </Button>
  );
  /** 目标行是否可转接：坐席/话机需分机且实时状态=示闲（非空闲置灰 + 原因 Tooltip） */
  const targetBlocked = (r: AgentProfile) => {
    const st = agentStates[r.id]?.state || r.state;
    const blocked = !!st && st !== 'idle';
    return { st, blocked };
  };

  // 进入面板时按需懒加载：技能组 + 坐席实时状态（兼作坐席/话机名册）+ 队列空闲数
  // （不拉 /users——admin-only 坐席无权限，坐席/话机展示直接用坐席字段）
  const ensureLoad = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    try {
      const [g, m, q] = await Promise.all([
        listSkillGroups({ page: 1, pageSize: 100 }),
        listMonitorAgents(true),
        listMonitorQueues(true),
      ]);
      setGroups(g.list || []);
      setMonitorAgents(m || []);
      const am: Record<number, MonitorAgent> = {};
      (m || []).forEach((x) => (am[x.agent_id] = x));
      setAgentStates(am);
      const qm: Record<string, MonitorQueue> = {};
      (q || []).forEach((x) => (qm[x.name] = x));
      setQueueLive(qm);
    } catch {
      /* 拦截器已提示 */
    } finally {
      setLoading(false);
    }
  }, []);

  // 打开弹窗立即加载一次（不用等鼠标悬浮），期间渲染骨架屏
  useEffect(() => {
    void ensureLoad();
  }, [ensureLoad]);

  const doTransfer = async (target: string, ring?: RingTarget, label?: string) => {
    if (!target) {
      Message.warning('请选择转接目标');
      return;
    }
    // 提示文案用显示名/分机名，不暴露队列分机后缀（`<code>_queue`）
    const shown = label || target;
    // 咨询邀请：hold 客户后拨第二路；技能组走 queue:<code>，不要把技能组当 SIP 号码
    if (intent === 'consult') {
      try {
        await inviteExpert(target, shown);
        onClose();
      } catch {
        /* 拦截器已提示 */
      }
      return;
    }
    // 来电振铃态转接（后端 /cmd/transfer-call 受控转接：客户腿入队 + 当前振铃腿挂断）：
    // 未接听时 SIP 会话尚未建立、发不出 REFER，故一律由后端代转——
    // 技能组传队列键 code（不是队列分机名 `${code}_queue`，也不是可改的显示名）；
    // 坐席/话机传分机号（后端解析其坐席与技能组并入队，条目只振铃该分机）。
    // uuid 由常驻顶栏从 WS 采集（面板自身收不到"进来之前"的振铃事件）。
    if (status === 'ringing-in') {
      if (!ring?.queue && !ring?.extension) {
        Message.warning('请选择转接目标技能组或分机');
        return;
      }
      const uuid = ringCallUUIDRef.current;
      if (!uuid) {
        Message.warning('未定位到当前来电（来电事件未到达），可先接听再转接');
        return;
      }
      try {
        await executeAssistAction({
          action: 'transfer',
          uuid,
          queue: ring.queue,
          target_extension: ring.extension,
        });
        Message.success(
          ring.extension ? `已转接，来电正在呼叫「${shown}」` : `已转接，来电转入「${shown}」排队`,
        );
        onClose();
      } catch {
        /* 拦截器已提示 */
      }
      return;
    }
    // 仅通话中可转接（继续走 SIP REFER 盲转）
    if (status !== 'in-call') {
      Message.info('当前未在通话中，仅通话时才能转接');
      return;
    }
    try {
      await refer(target);
      Message.success(`已转接到 ${shown}`);
      onTransferred?.();
      onClose();
    } catch {
      /* 拦截器已提示 */
    }
  };

  // 行内「转接」按钮（三张表共用）：通话中盲转是 SIP REFER，发出后无法撤回——客户会直接被转走，
  // 所以行内点按先弹一次确认（写清目标），禁用态保持原样不弹。
  // ring：振铃态（未接听）的目标——技能组给 queue（队列键 code）、坐席/话机给 extension（分机号），
  // 两者都走后端受控转接；接听后一律走 SIP REFER。
  const transferButton = (target: string, label: string, btnDisabled: boolean, ring?: RingTarget) => (
    <Popconfirm
      title={
        intent === 'consult'
          ? `确认邀请「${label}」？客户将先被保持；接通后请完成转接或取消（三方将在引擎升级后提供）。`
          : status === 'ringing-in'
            ? ring?.extension
              ? `确认转接到「${label}」？客户将直接呼叫该分机，对方未接听时回到其技能组排队。`
              : `确认转接到「${label}」？未接听的来电将转入该技能组重新排队。`
            : `确认转接到「${label}」？盲转发出后无法撤回。`
      }
      okText={intent === 'consult' ? '确认邀请' : '确认转接'}
      cancelText="取消"
      disabled={btnDisabled}
      onOk={() => doTransfer(target, ring, label)}
    >
      <Button size="small" type="text" className="bcc-shift-go" disabled={btnDisabled}>
        {intent === 'consult' ? '邀请' : '转接'}
      </Button>
    </Popconfirm>
  );

  // 实时状态 → 胶囊（文案/色单源 labels.ts；无数据按离线处理）
  const stateTag = (state?: string) => {
    const [text, color] = transferAgentStateMeta(state || 'offline');
    return <PillTag color={color}>{text}</PillTag>;
  };

  const groupCols = useMemo(
    () => [
      {
        key: 'fav',
        title: '',
        width: 34,
        render: (_: any, r: SkillGroup) => favStar(`g:${r.code || r.name}`),
      },
      {
        key: 'name',
        title: '技能组名称',
        dataIndex: 'name',
        width: 150,
        render: (v: string) => <span style={{ fontWeight: 500, color: 'var(--bcc-text-1)' }}>{v}</span>,
      },
      {
        key: 'idle_count',
        title: '空闲坐席',
        width: 90,
        render: (_: any, r: SkillGroup) => {
          // 队列实时状态按队列键 code 索引（显示名改名不影响队列身份，见 queue.Status.Name 口径）
          const q = queueLive[r.code];
          // 空闲坐席数=统计值，按 D3 用普通文字（等宽）呈现，不套标签
          return q ? <span className="bcc-mono">{q.agents_idle}</span> : '-';
        },
      },
      {
        key: 'action',
        title: '操作',
        width: 70,
        align: 'right' as const,
        // 转接目标一律用队列键 code：通话中盲转 REFER 到 dialplan 的 `<code>_queue`，
        // 振铃态后端按 code 入队（显示名可改，不能作目标）
        render: (_: any, r: SkillGroup) =>
          transferButton(intent === 'consult' ? `queue:${r.code}` : `${r.code}_queue`, r.name, !!disabled, { queue: r.code }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queueLive, disabled, favs, status, intent],
  );

  const agentCols = useMemo(
    () => [
      {
        key: 'fav',
        title: '',
        width: 34,
        render: (_: any, r: AgentProfile) => favStar(`a:${r.extension || r.id}`),
      },
      {
        key: 'username',
        title: '工号',
        width: 80,
        render: (_: any, r: AgentProfile) => r.username || r.name || `#${r.id}`,
      },
      {
        key: 'name',
        title: '姓名',
        width: 80,
        render: (_: any, r: AgentProfile) => r.name || r.username || '-',
      },
      {
        key: 'state',
        title: '状态',
        width: 90,
        render: (_: any, r: AgentProfile) => stateTag(agentStates[r.id]?.state || r.state),
      },
      {
        key: 'action',
        title: '操作',
        width: 70,
        align: 'right' as const,
        render: (_: any, r: AgentProfile) => {
          const { st, blocked } = targetBlocked(r);
          if (blocked) {
            return (
              <Tooltip content={`${transferAgentStateMeta(st)[0]}，暂不可${intent === 'consult' ? '邀请' : '转接'}`}>
                <Button size="small" type="text" className="bcc-shift-go" disabled>
                  {intent === 'consult' ? '邀请' : '转接'}
                </Button>
              </Tooltip>
            );
          }
          return transferButton(
            String(r.extension),
            r.name || r.username || `分机 ${r.extension}`,
            !!disabled || !r.extension,
            { extension: r.extension }
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agentStates, disabled, favs, status, intent],
  );

  // 话机：以坐席分机（agent.extension）作为话机实体——项目无独立话机表，分机即话机
  const extensionCols = useMemo(
    () => [
      {
        key: 'fav',
        title: '',
        width: 34,
        render: (_: any, r: AgentProfile) => favStar(`a:${r.extension || r.id}`),
      },
      {
        key: 'extension',
        title: '分机号',
        width: 70,
        dataIndex: 'extension',
        render: (v: string) => (v ? <Typography.Text bold>{v}</Typography.Text> : '-'),
      },
      {
        key: 'name',
        title: '话机名称',
        width: 80,
        render: (_: any, r: AgentProfile) => r.name || r.username || `分机 ${r.extension}`,
      },
      {
        key: 'binding',
        title: '绑定关系',
        width: 90,
        render: (_: any, r: AgentProfile) =>
          r.username ? `${r.name || r.username}（${r.username}）` : '-',
      },
      {
        key: 'state',
        title: '状态',
        width: 80,
        render: (_: any, r: AgentProfile) => stateTag(agentStates[r.id]?.state || r.state),
      },
      {
        key: 'action',
        title: '操作',
        width: 70,
        align: 'right' as const,
        render: (_: any, r: AgentProfile) => {
          const { st, blocked } = targetBlocked(r);
          if (blocked) {
            return (
              <Tooltip content={`${transferAgentStateMeta(st)[0]}，暂不可${intent === 'consult' ? '邀请' : '转接'}`}>
                <Button size="small" type="text" className="bcc-shift-go" disabled>
                  {intent === 'consult' ? '邀请' : '转接'}
                </Button>
              </Tooltip>
            );
          }
          return transferButton(
            String(r.extension),
            r.name || r.username || `分机 ${r.extension}`,
            !!disabled || !r.extension,
            { extension: r.extension }
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agentStates, disabled, favs, status, intent],
  );

  // 容器固定高度（表头吸顶 + 数据区滚动），消除 loading / 空态 / 列表 三态切换的高度抖动；
  // 面板已加宽到 540：表格仅限纵向滚动（行数超出可视区），不再设横向 scroll.x——
  // 列宽自适应撑满容器，杜绝窄面板下的横向滚动条
  const PANEL_BODY_HEIGHT = 360;
  const emptyText =
    tab === 'skill_group'
      ? intent === 'consult'
        ? '暂无可邀请技能组'
        : '暂无可转接技能组'
      : tab === 'agent'
        ? intent === 'consult'
          ? '暂无可邀请坐席'
          : '暂无可转接坐席'
        : intent === 'consult'
          ? '暂无可邀请话机'
          : '暂无可转接话机';

  const renderTable = () => {
    if (loading) {
      return <SkeletonTable rows={6} cols={tab === 'skill_group' ? 4 : tab === 'agent' ? 5 : 6} />;
    }
    if (tab === 'skill_group') {
      // 排序：收藏置顶 → 空闲坐席数降序（无队列数据排后）；键一律取队列键 code
      const rows = [...groups].sort((a, b) => {
        const fa = favs.includes(`g:${a.code || a.name}`) ? 1 : 0;
        const fb = favs.includes(`g:${b.code || b.name}`) ? 1 : 0;
        if (fa !== fb) return fb - fa;
        return Number(queueLive[b.code]?.agents_idle || 0) - Number(queueLive[a.code]?.agents_idle || 0);
      });
      return rows.length === 0 ? (
        <div className="bcc-shift-empty">{emptyText}</div>
      ) : (
        <Table
          key={`skill_group_${tab}`}
          rowKey="id"
          size="small"
          pagination={false}
          columns={groupCols}
          data={rows}
          scroll={{ y: PANEL_BODY_HEIGHT - 44 }}
          tableLayoutFixed
        />
      );
    }
    if (agents.length === 0) {
      return <div className="bcc-shift-empty">{emptyText}</div>;
    }
    // 坐席/话机：先按关键字收敛（名册是全量坐席，不搜索无法定位），再排序：收藏置顶 → 示闲优先
    const kw = targetKw.trim().toLowerCase();
    const scoped = kw
      ? agents.filter((r) => `${r.name || ''} ${r.extension || ''}`.toLowerCase().includes(kw))
      : agents;
    if (scoped.length === 0) {
      return <div className="bcc-shift-empty">没有匹配「{targetKw.trim()}」的坐席</div>;
    }
    const rank = (r: AgentProfile) => {
      const st = agentStates[r.id]?.state || r.state || '';
      const fav = favs.includes(`a:${r.extension || r.id}`) ? 0 : 1;
      const idle = st === 'idle' ? 0 : 1;
      return fav * 2 + idle * 4;
    };
    const rows = [...scoped].sort((a, b) => rank(a) - rank(b));
    return (
      <Table
        key={`${tab}`}
        rowKey="id"
        size="small"
        pagination={false}
        columns={tab === 'agent' ? agentCols : extensionCols}
        data={rows}
        scroll={{ y: PANEL_BODY_HEIGHT - 44 }}
        tableLayoutFixed
      />
    );
  };

  return (
    <div className="bcc-shift-panel" onMouseEnter={ensureLoad}>
      <div className="bcc-shift-tabs" role="tablist" aria-label="转接目标类型">
        {SHIFT_TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            className={`bcc-shift-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* 坐席/话机名册是全量坐席，给一个搜索框才谈得上「挑人」；技能组数量少，不需要 */}
      {tab !== 'skill_group' && (
        <div className="bcc-shift-search">
          <Input
            size="small"
            allowClear
            placeholder="搜索姓名或分机"
            value={targetKw}
            onChange={(v) => setTargetKw(String(v || ''))}
          />
        </div>
      )}
      <div className="bcc-shift-body">{renderTable()}</div>
    </div>
  );
}
